import React from 'react'
import type { Theme } from '@mui/material/styles'
import { createTheme as muiCreateTheme, useTheme } from '@mui/material/styles'
import merge from 'lodash/merge'
import { makeStyles, withStyles } from 'tss-react/mui'

import { CAPACITY_BOX_COLORS, COLORS, COLORS2 } from '~/common/globalStyles'

export { useTheme }
export { makeStyles, withStyles }
export type { Theme } from '@mui/material/styles'

export function withInjectedTheme<P extends { theme: Theme }>(
  Component: React.ComponentType<P>,
): React.ComponentType<Omit<P, 'theme'>> {
  const Wrapped = (props: Omit<P, 'theme'>) => {
    const theme = useTheme()
    return React.createElement(Component, { ...props, theme } as P)
  }
  Wrapped.displayName = `WithInjectedTheme(${Component.displayName ?? Component.name ?? 'Component'})`
  return Wrapped
}

declare module '@mui/material/styles' {
  interface Palette {
    current: string
    offBackground: {
      default: string
      soft: string
      column: string
      wrapper: string
      notSelectedTab: string
      internalBackground: string
      headerBackground: string
      selectedTask: string
      nonExistsAttendee: string
      hoverBorderAttendee: string
      accordionBackground: string
      timeSelectorBackground: string
      rowBackground: string
    }

    borders: {
      softBorder: string
      altoGray: string
      altoGray50: string
    }

    status: {
      notStarted: string
      workingOnIt: string
      stuck: string
      signOff: string
      purple: string
      notScheduled: string
      scheduled: string
      done: string
      notApplicable: string
      onTime: string
    }

    pills: {
      lightGreen: string
      mediumGreen: string
      lightBlue: string
      mediumBlue: string
    }

    indicators: {
      green: string
      red: string
    }

    specialGrey: {
      350: string
      430: string
      500: string
      1000: string
    }

    blue: {
      500: string
    }

    green: {
      500: string
    }

    sky: {
      300: string
      500: string
      600: string
      700: string
    }

    olive: {
      500: string
    }

    red: {
      300: string
      500: string
      700: string
    }

    orange: {
      400: string
      500: string
    }

    purple: {
      500: string
      700: string
    }

    sage: {
      400: string
      500: string
      600: string
      700: string
    }

    glacier: {
      400: string
      500: string
      600: string
    }

    spruce: {
      400: string
      500: string
      600: string
    }

    dust: {
      400: string
      500: string
      600: string
    }

    lighterGray: string
    darkerGray: string
    altoGrey: string
    white: string
    watermelon: string
    mint: string
    placeholder: string
  }

  interface Theme {
    name: string
  }
}

const DEFAULT_THEME_V2 = {
  name: 'lightV2',

  spacing: 8,

  typography: {
    fontFamily: ['Graphik', 'sans-serif'].join(','),
    body1: {
      fontSize: '14px',
    },
    body2: {
      fontSize: '12px',
    },
  },

  palette: {
    mode: 'light' as const,

    current: COLORS2.ash[500],

    pills: {
      lightGreen: CAPACITY_BOX_COLORS.lightGreen,
      mediumGreen: CAPACITY_BOX_COLORS.mediumGreen,
      lightBlue: CAPACITY_BOX_COLORS.lightBlue,
      mediumBlue: CAPACITY_BOX_COLORS.mediumBlue,
    },

    indicators: {
      green: COLORS.mediumGreen,
      red: COLORS.lightRed,
    },

    // mui
    primary: {
      light: '#8F8F8F', // ash 300
      main: '#333333', // ash 500
      dark: '#1F1F1F', // ash 700
    },

    secondary: {
      main: '#942110', // error 700
    },

    success: {
      light: '#B8E58A', // 300
      main: '#8FCE50', // 500
      dark: '#52861D', // 700
    },

    error: {
      light: '#F18D7E', // 300
      main: '#E94D35', // 500
      dark: '#942110', // 700
    },

    warning: {
      light: '#FFE371', // 300
      main: '#F5C400', // 500
      dark: '#A36200', // 700
    },

    grey: {
      altoGrey: COLORS2.gray[30],
      25: '#FAFAFA',
      50: '#F7F7F7',
      100: '#F2F2F2',
      200: '#E0E0E0',
      300: '#B8B8B8',
      400: '#A3A3A3',
      500: '#8F8F8F',
      600: '#666666',
      700: '#525252',
      800: '#474747',
      900: '#3D3D3D',
    },

    specialGrey: {
      350: COLORS2.gray[350],
      430: COLORS.gray6,
      500: COLORS.gray5,
      1000: COLORS2.gray[1000],
    },

    offBackground: {
      default: COLORS.offWhite,
      soft: COLORS2.gray[10],
      column: COLORS.gray2,
      wrapper: 'white',
      notSelectedTab: '#eeeeee',
      internalBackground: COLORS.beige,
      headerBackground: '#F6F7F9',
      selectedTask: '#EAEAEA',
      nonExistsAttendee: '#FFFAE8',
      hoverBorderAttendee: '#dddddd',
      accordionBackground: COLORS.offWhite,
      timeSelectorBackground: COLORS.gray3,
      rowBackground: '#f6f6f6',
    },

    borders: {
      softBorder: '#E8E8E8',
      altoGray: COLORS2.gray[30],
      altoGray50: COLORS2.gray[50],
    },

    orange: {
      400: '#E59662',
      500: '#DF7F3F',
    },

    blue: {
      500: COLORS2.blue[500],
    },

    sky: {
      300: '#87C9E9',
      500: '#42ABDC',
      600: '#2080AC',
      700: '#1A668A',
    },

    olive: {
      300: '#C4CFA0',
      500: '#A2B36A',
      700: '#606C37',
    },

    sage: {
      300: '#B4D49B',
      400: '#9FC87F',
      500: '#8ABB62',
      600: '#628E3E',
      700: '#52792A',
    },

    glacier: {
      300: '#93BEDC',
      400: '#74ACD2',
      500: '#5599C8',
      600: '#32709A',
      700: '#28597C',
    },

    spruce: {
      300: '#A2BFCD',
      400: '#22CCEE',
      500: '#06AED4',
      600: '#088AB2',
      700: '#395A6A',
    },

    dust: {
      300: '#B5B9BA',
      400: '#A0A4A7',
      500: '#8A9093',
      600: '#62676A',
      700: '##4E5355',
    },

    green: {
      500: '#8FC559',
    },

    red: {
      300: '#F18D7E',
      500: '#D65A47',
      700: '#862B1E',
    },

    purple: {
      500: '#9C6BB3',
      700: '#5B376C',
    },

    lighterGray: COLORS.lighterGray,

    darkerGray: COLORS.darkerGray,

    white: COLORS.white,
    watermelon: COLORS.watermelon,
    mint: COLORS.mint,

    text: {
      primary: '#333333',
      secondary: '#8F8F8F',
    },

    divider: '#DDDDDD',

    action: {
      hover: COLORS.lightestGray,
    },

    status: {
      notStarted: COLORS.darkGray,
      workingOnIt: COLORS.blue,
      stuck: COLORS.red,
      signOff: COLORS.purple,
      notScheduled: COLORS.darkGray,
      scheduled: COLORS.blue,
      done: COLORS.green,
      notApplicable: COLORS.lightGray,
      onTime: COLORS.lightBlue,
      onHold: COLORS.purple,
      late: COLORS.red,
      cancelled: COLORS.darkGray,
      /*
      **** - these colors are the correct colors for the new theme.  
      We are rolling back to allow support more time to make cust aware of new colors.
      https://guidecx.atlassian.net/browse/SUP-856

      notStarted: '#8ABB62',
      workingOnIt: '#42ABDC',
      stuck: '#D65A47',
      signOff: '#9C6BB3',
      notScheduled: '#8ABB62',
      scheduled: '#42ABDC',
      done: '#333333',
      notApplicable: '#E0E0E0',
      onTime: '#42ABDC',
      onHold: '#E0E0E0',
      late: '#D65A47',
      cancelled: '#A3A3A3',
      */
    },

    placeholder: COLORS2.gray[100],
  },

  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: COLORS.blue,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          /**
           * Material UI 4.8 introduced a change to make menu items smaller on
           * desktop to better match Material Design specifications
           * https://github.com/mui-org/material-ui/pull/17332
           *
           * This is a global override for that change
           * **/
          '@media (min-width: 600px)': {
            minHeight: 48,
          },
        },
        dense: {
          /**
           * Material UI 4.8 started using 'min-height: auto;' which ignores any
           * other styling applied, so min-height is always 0px. This is intentional
           * but it is a breaking change from the way it was behaving. This overrides that
           * so that even with dense menus it uses the initial value from MuiMenuItem-root
           *
           * TODO: remove this override when we restyle our components
           */
          minHeight: '',
        },
      },
    },
  },
}

const createTheme = (overrides: Record<string, unknown> = {}): Theme => {
  const currentTheme = merge({}, DEFAULT_THEME_V2, overrides)
  return muiCreateTheme(currentTheme)
}

export default createTheme
